import React, { useEffect, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import AuthService from '../services/AuthService';
import { ButtonSpinner, PanelLoader, TableEmptyState } from '../components/LoadingStates';
import { roleLabels } from '../utils/labels';

const emptyUser = { name: '', phone: '', email: '', password: '', role: 'employee' };

const Users = () => {
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState(emptyUser);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [editingId, setEditingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const rows = await AuthService.listUsers();
            setUsers(rows);
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible cargar usuarios.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage('');
        try {
            const payload = { ...form };
            if (editingId && !payload.password) {
                delete payload.password;
            }
            if (editingId) {
                await AuthService.updateUser(editingId, payload);
            } else {
                await AuthService.createUser(payload);
            }
            setForm(emptyUser);
            setEditingId(null);
            setMessage(editingId ? 'Usuario actualizado.' : 'Usuario creado.');
            await load();
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible guardar el usuario.');
        } finally {
            setSaving(false);
        }
    };

    const editUser = (item) => {
        setEditingId(item.id);
        setForm({
            name: item.name || '',
            phone: item.phone || '',
            email: item.email || '',
            password: '',
            role: item.role || 'employee',
            active: Boolean(item.active),
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm(emptyUser);
    };

    const deleteUser = async (item) => {
        if (!window.confirm(`Eliminar usuario ${item.name}?`)) return;
        setSaving(true);
        setMessage('');
        try {
            await AuthService.deleteUser(item.id);
            setMessage('Usuario eliminado.');
            await load();
        } catch (error) {
            setMessage(error.response?.data?.message || 'No fue posible eliminar el usuario.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="two-column">
            <form className={`panel ${saving ? 'is-busy' : ''}`} onSubmit={submit}>
                {saving && <PanelLoader label={editingId ? 'Actualizando usuario...' : 'Creando usuario...'} />}
                <div className="form-title-row">
                    <h2>{editingId ? 'Editar usuario' : 'Nuevo usuario'}</h2>
                    {editingId && <button className="ghost-button muted" type="button" onClick={cancelEdit}><FiX /><span>Cancelar</span></button>}
                </div>
                {message && <div className="notice">{message}</div>}
                <label>Nombre<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
                <label>Telefono<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></label>
                <label>Correo<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></label>
                <label>Contrasena<input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editingId} placeholder={editingId ? 'Dejar en blanco para no cambiar' : ''} /></label>
                <label>Rol<select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="employee">Empleado</option><option value="admin">Administrador</option></select></label>
                {editingId && (
                    <label>Estado<select value={form.active ? 'active' : 'inactive'} onChange={(e) => setForm({ ...form, active: e.target.value === 'active' })}><option value="active">Activo</option><option value="inactive">Inactivo</option></select></label>
                )}
                <button className="primary-button" type="submit" disabled={saving}>
                    {saving ? <ButtonSpinner /> : <FiPlus />}
                    <span>{saving ? 'Guardando...' : (editingId ? 'Actualizar' : 'Crear')}</span>
                </button>
            </form>
            <div className="panel">
                <h2>Usuarios</h2>
                <div className="table-wrap">
                    {loading || users.length === 0 ? (
                        <TableEmptyState loading={loading} emptyText="No hay usuarios para mostrar." />
                    ) : (
                        <table>
                            <thead><tr><th>Nombre</th><th>Correo</th><th>Rol</th><th>Estado</th><th>Acciones</th></tr></thead>
                            <tbody>{users.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.name}</td>
                                    <td>{item.email}</td>
                                    <td>{roleLabels[item.role] || item.role}</td>
                                    <td>{item.active ? 'Activo' : 'Inactivo'}</td>
                                    <td>
                                        <div className="row-actions">
                                            <button className="icon-button" type="button" onClick={() => editUser(item)} aria-label="Editar usuario"><FiEdit2 /></button>
                                            <button className="icon-button danger" type="button" onClick={() => deleteUser(item)} aria-label="Eliminar usuario"><FiTrash2 /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}</tbody>
                        </table>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Users;
